import React, { useMemo, useState } from "react";
import { Calculator, IndianRupee, Percent, Calendar } from "lucide-react";
import ToolCard from "./ToolCard";
import { formatINR } from "./formatters";

// Monthly SIP future value formula:
// FV = P * [((1+i)^n - 1)/i] * (1+i)
function sipFutureValue(monthly, annualRate, months) {
  const P = Number(monthly);
  const r = Number(annualRate) / 12 / 100;
  const n = Number(months);
  if (P <= 0 || n <= 0) return { fv: 0, invested: 0, gain: 0 };
  if (r === 0) {
    const invested = P * n;
    return { fv: invested, invested, gain: 0 };
  }
  const fv = P * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
  const invested = P * n;
  return { fv, invested, gain: fv - invested };
}

export default function SIPCalculator() {
  const [sip, setSip] = useState(10000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);

  const months = useMemo(() => Math.max(1, Math.round(Number(years) * 12)), [years]);
  const { fv, invested, gain } = useMemo(
    () => sipFutureValue(Number(sip), Number(rate), months),
    [sip, rate, months]
  );

  return (
    <ToolCard
      title="SIP Calculator"
      subtitle="Estimate how monthly SIPs can grow over time using expected return."
    >
      <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-950">
        <Calculator className="h-4 w-4" />
        Monthly investing
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <Field
          label="Monthly SIP"
          icon={<IndianRupee className="h-4 w-4 text-slate-500" />}
          value={sip}
          onChange={setSip}
          min={0}
        />
        <Field
          label="Expected Return (p.a.)"
          icon={<Percent className="h-4 w-4 text-slate-500" />}
          value={rate}
          onChange={setRate}
          min={0}
          step="0.1"
        />
        <Field
          label="Time (years)"
          icon={<Calendar className="h-4 w-4 text-slate-500" />}
          value={years}
          onChange={setYears}
          min={0}
          step="1"
        />
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <ResultBox title="Future Value" value={formatINR(fv)} highlight />
        <ResultBox title="Total Invested" value={formatINR(invested)} />
        <ResultBox title="Estimated Gain" value={formatINR(gain)} />
      </div>

      <p className="mt-3 text-xs text-slate-500">
        Note: Returns are not guaranteed. This is a projection based on assumed rate.
      </p>
    </ToolCard>
  );
}

function Field({ label, icon, value, onChange, min = 0, step = "1" }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-700">{label}</label>
      <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
        {icon}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm outline-none"
          min={min}
          step={step}
        />
      </div>
    </div>
  );
}

function ResultBox({ title, value, highlight }) {
  return (
    <div className={`rounded-2xl p-4 ${highlight ? "bg-emerald-50" : "bg-slate-50"}`}>
      <div className={`text-xs ${highlight ? "text-emerald-900/70" : "text-slate-600"}`}>{title}</div>
      <div className={`mt-1 text-lg font-semibold ${highlight ? "text-emerald-950" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}
